<template>
  <nav class="navbar" v-show="isNavbarVisible">
    <div class="navbar-container">
      <!-- Wrap logo and brand name with a conditional router-link -->
      <router-link :to="isAuthenticated ? '/timeline' : '/'" class="navbar-brand">
        <img :src="logoSrc" alt="Logo" class="logo" />
        <span class="brand-name">{{ brandName }}</span>
      </router-link>
      <div class="navbar-links">
        <!-- Display user info if authenticated -->
        <div v-if="isAuthenticated" class="navbar-user">
          <!-- Remove post button for small screens -->
          <button v-show="!isMobile" @click="onButtonClick" class="navbar-button">Post</button>
          
          <!-- Notifications icon -->
          <div class="notifications-icon" :class="{ 'has-notifications': unreadNotifications > 0 }" @click="toggleNotifications">
            <i class="fas fa-bell"></i>
            <span v-if="unreadNotifications" class="notification-badge">{{ unreadNotifications }}</span>
          </div>
          
          <div class="dropdown" @click="toggleDropdown">
            <img :src="user.avatar" alt="Profile Picture" class="profile-pic" />
            <span v-show="!isMobile" class="username">{{ user.username }}</span>
            <i class="fas fa-caret-down"></i>
            <div v-if="dropdownOpen" class="dropdown-menu">
              <router-link v-if="$route.path !== '/timeline'" to="/timeline" class="dropdown-item timeline-item">
                <i class="fas fa-home"></i>
                <span>Timeline</span>
              </router-link>
              <router-link v-if="$route.path !== `/${user.username}`" :to="`/${user.username}`" class="dropdown-item profile-item" :key="user.username">
                <img :src="user.avatar" alt="Profile Picture" class="dropdown-avatar" />
                Profile
              </router-link>
              <router-link v-if="$route.path !== '/post/create'" to="/post/create" class="dropdown-item">
                <i class="fas fa-plus"></i>
                <span>Create Post</span>
              </router-link>

              <div class="dropdown-separator"></div>

              <router-link v-if="$route.path !== '/all-skiers'" to="/all-skiers" class="dropdown-item">
                <i class="fas fa-users"></i>
                <span>All Skiers</span>
              </router-link>
              <router-link v-if="isWeb && !isMobile && $route.path !== '/request-pro-tip'" to="/request-pro-tip" class="dropdown-item">
                <i class="fas fa-lightbulb"></i>
                <span>Pro Tips</span>
              </router-link>
              <a v-if="(!isWeb || isMobile) && $route.path !== '/request-pro-tip'" href="#" @click.prevent="openProTipInBrowser" class="dropdown-item">
                <i class="fas fa-lightbulb"></i>
                <span>Pro Tips</span>
              </a>
              <router-link v-if="$route.path !== '/all-tools'" to="/all-tools" class="dropdown-item contact-item">
                <i class="fas fa-th-large"></i>
                <span>All Tools</span>
              </router-link>
            </div>
          </div>
        </div>
        <!-- Display "Login" if not authenticated -->
        <div v-else>
          <a href="create-account" class="navbar-link create-account-link">Create Account</a>
          <button @click="goToLogin" class="navbar-button">Login</button>
        </div>
      </div>
    </div>
  </nav>

  <!-- Sticky Bottom Navbar for Mobile (only for signed in users) -->
  <nav v-if="isAuthenticated && isMobile" class="bottom-navbar">
    <router-link to="/timeline" class="bottom-navbar-item">
      <i class="fas fa-home"></i>
      <span>Timeline</span>
    </router-link>
    <router-link to="/post/create" class="bottom-navbar-item">
      <i class="fas fa-plus-circle"></i>
      <span>Create Post</span>
    </router-link>
    <router-link to="/all-tools" class="bottom-navbar-item">
      <i class="fas fa-th-large"></i>
      <span>All Tools</span>
    </router-link>
  </nav>
</template>

<script>
import { API_BASE_URL, WEB_BASE_URL } from '@/config';
import axios from 'axios';
import eventBus from '@/utils/eventBus';
import { Capacitor } from '@capacitor/core';

export default {
  name: 'NavBar',
  props: {
    logoSrc: {
      type: String,
      required: true,
    },
    brandName: {
      type: String,
      required: true,
    },
    links: {       
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isAuthenticated: false,
      user: {
        avatar: '',
        username: '',
      },
      dropdownOpen: false,
      lastScrollY: 0,
      isNavbarVisible: true,
      isWeb: false,
      isIOS: false,
      isAndroid: false,
      platformClass: '',
      unreadNotifications: 0,
      notificationsOpen: false,
    };
  },
  created() {
    this.checkAuthentication();
    const platform = Capacitor.getPlatform();
    this.isWeb = platform === 'web';
    this.isIOS = platform === 'ios';
    this.isAndroid = platform === 'android';
    this.platformClass = `platform-${platform}`;
    document.body.classList.add(this.platformClass);

    // Add document click listener
    document.addEventListener('click', this.handleClickOutside);

    // Listen for the userLoggedIn event
    eventBus.on('userLoggedIn', this.checkAuthentication);

    // Listen for logout events
    eventBus.on('userLoggedOut', this.handleLogout);

    // Add resize and scroll event listeners
    window.addEventListener('resize', this.checkIfMobile);

    // Initial check
    this.checkIfMobile();
    
    // Fetch notifications when component is created
    if (localStorage.getItem('access_token')) {
      this.fetchNotifications();
    }
    
    // Set up interval to periodically check for new notifications (every 60 seconds)
    this.notificationInterval = setInterval(() => {
      if (this.isAuthenticated) {
        this.fetchNotifications();
      }
    }, 60000);
    
    // Listen for notification-read events using eventBus instead of $root
    eventBus.on('notification-read', this.fetchNotifications);
  },
  beforeUnmount() {
    // Clean up event listeners
    window.removeEventListener('resize', this.checkIfMobile);
    document.removeEventListener('click', this.handleClickOutside);
    
    // Clear notification interval
    clearInterval(this.notificationInterval);
    
    // Remove eventBus listeners
    eventBus.off('notification-read', this.fetchNotifications);
    eventBus.off('userLoggedIn', this.checkAuthentication);
    eventBus.off('userLoggedOut', this.handleLogout);
  },
  methods: {
    async checkAuthentication() {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          const response = await axios.get(`${API_BASE_URL}/api/profile/`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          this.user = response.data;
          this.isAuthenticated = true;
          
          // Fetch notifications after successful authentication
          this.fetchNotifications();
        } catch (error) {
          console.error('Error fetching user data:', error);
          this.isAuthenticated = false;
        }
      } else {
        this.isAuthenticated = false;
      }
    },
    async fetchNotifications() {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      
      try {
        const response = await axios.get(`${API_BASE_URL}/api/notifications/`, {
          headers: {
            Authorization: `Bearer ${token}`
          },
          params: {
            limit: 20,
            is_read: false // Only fetch unread notifications
          }
        });
        
        // Update unread notifications count
        this.unreadNotifications = response.data.length;
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    },
    toggleDropdown(event) {
      // Stop the click event from propagating to document
      event.stopPropagation();
      this.dropdownOpen = !this.dropdownOpen;
    },
    handleClickOutside(event) {
      // Close dropdown if click is outside the dropdown area
      if (this.dropdownOpen && !event.target.closest('.dropdown')) {
        this.dropdownOpen = false;
      }
    },
    logout() {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      this.isAuthenticated = false;
      this.$router.push('/login');
    },
    onButtonClick() {
      this.$router.push('/post/create');
    },
    goToLogin() {
      this.$router.push('/login');
    },
    checkIfMobile() {
      this.isMobile = window.innerWidth <= 600;
    },
    toggleNotifications(event) {
      event.stopPropagation();
      
      // Navigate to notifications page
      this.$router.push('/notifications');
      
      // Don't mark notifications as read automatically
      // this.markNotificationsAsRead();
    },
    async markNotificationsAsRead() {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      
      try {
        // This is a placeholder - you would need to implement this endpoint
        // await axios.post(`${API_BASE_URL}/api/notifications/mark-read/`, {}, {
        //   headers: {
        //     Authorization: `Bearer ${token}`
        //   }
        // });
        
        // For now, just reset the counter locally
        this.unreadNotifications = 0;
      } catch (error) {
        console.error('Error marking notifications as read:', error);
      }
    },
    openProTipInBrowser() {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      
      // Generate a temporary state parameter for security
      const state = Math.random().toString(36).substring(2, 15);
      
      // Store the state in localStorage to verify later
      localStorage.setItem('auth_state', state);
      
      // Create a secure hash of the token (this is a simple example - in production you'd want a more secure approach)
      // For this example, we'll just use the token directly, but in a real app you might want to encrypt it
      
      // Open the URL with the token as a query parameter
      const url = `${WEB_BASE_URL}/request-pro-tip?token=${encodeURIComponent(token)}&state=${state}`;
      window.open(url, '_blank');
    },
    handleLogout() {
      this.isAuthenticated = false;
    },
  },
};
</script>

<style scoped>
/* General Styles */
body {
  margin: 0;
  background-color: #15202b;
  color: #d9d9d9;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

/* Navbar Styles */
.navbar {
  background-color: #192734;
  padding: 10px 20px;
  border-bottom: 1px solid #38444d;
  transition: transform 0.3s ease, opacity 0.3s ease;
}

/* Safe Area for iOS Devices */
body.platform-ios .navbar {
  background-color: #192734; /* Same as existing navbar background */
  @supports (padding: env(safe-area-inset-top)) {
    padding-top: calc(env(safe-area-inset-top) + 60px) !important;
  }
}

/* Safe Area for Android Devices (edge-to-edge) */
body.platform-android .navbar {
  @supports (padding: env(safe-area-inset-top)) {
    padding-top: calc(env(safe-area-inset-top) + 10px);
  }
}

/* Visibility transitions */
[v-show="isNavbarVisible"] {
  opacity: 1;
  transform: translateY(0);
}

[v-show="isNavbarVisible"]:not([v-show]) {
  opacity: 0;
  transform: translateY(-100%);
}

.navbar-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
}

.navbar-brand {
  display: flex;
  align-items: center;
  text-decoration: none;
}

.logo {
  width: 40px;
  height: auto;
  margin-right: 10px;
}

.brand-name {
  font-size: 1.5em;
  font-weight: bold;
  color: white;
}

.navbar-links {
  display: flex;
  align-items: center;
}

.navbar-link {
  color: #ffffff;
  font-size: 1em;
  font-weight: bold;
  text-decoration: none;
  margin-right: 20px;
  transition: color 0.3s ease;
}

.navbar-link:hover {
  color: #1da1f2;
}

/* Media query for small screens */
@media (max-width: 600px) {
  .create-account-link {
    display: none;
  }
  
  .dropdown-menu {
    min-width: 200px;
  }
}

/* New Navbar Button Styles */
.navbar-button {
  padding: 8px 16px;
  background-color: #1da1f2;
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.navbar-button:hover {
  background-color: #0c8ddb;
}

/* User Profile and Dropdown Styles */
.navbar-user {
  position: relative;
  display: flex;
  align-items: center;
  margin-left: 20px;
}

.profile-pic {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  margin-left: 20px;
  border: 2px solid #1da1f2;
}

.username {
  font-size: 1em;
  font-weight: bold;
  color: #ffffff;
  margin-left: 10px;
}

.dropdown {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.fas.fa-caret-down {
  margin-left: 8px;
  color: #8899a6;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background-color: #192734;
  border: 1px solid #38444d;
  border-radius: 8px;
  margin-top: 8px;
  min-width: 180px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 15px;
  color: #ffffff;
  text-decoration: none;
  font-weight: bold;
  transition: background-color 0.3s ease;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
}

.dropdown-item:hover {
  background-color: #22303c;
}

.dropdown-item i {
  font-size: 1.1em;
  width: 18px;
  text-align: center;
  color: #d4e2ec;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.dropdown-item span {
  flex: 1;
  text-align: left;
}

/* Dropdown Avatar Styles */
.profile-item {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: -1.5px;
  padding-top: 14px;
}

.timeline-item {
  padding-top: 14px;
}

.contact-item {
  padding-bottom: 16px;
}

.dropdown-avatar {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid #1da1f2;
}

/* Dropdown Separator Styles */
.dropdown-separator {
  height: 1px;
  background-color: #38444d;
  margin: 8px 0;
}

/* Bottom Navbar for Mobile */
.bottom-navbar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #1927346e;
  border-top: 1px solid #38444d;
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 8px; /* Reduced padding */
  padding-bottom: 20px;
  transition: background-color 0.3s ease;
  z-index: 1000;
}


.bottom-navbar-item {
  text-align: center;
  color: white;
  text-decoration: none;
  font-size: 1em; /* Reduced font size */
}

.bottom-navbar-item i {
  font-size: 1.2em; /* Reduced icon size */
}

.bottom-navbar-item span {
  display: block;
  font-size: 0.7em; /* Reduced text size */
}

/* Notification Styles */
.notifications-icon {
  position: relative;
  margin-left: 20px;
  margin-top: 3px;
  cursor: pointer;
  color: #ffffff;
}

.notifications-icon.has-notifications {
  margin-top: 7px;
  margin-right: 4px;
}

.notifications-icon i {
  font-size: 1.2em;
}

.notification-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: #f91880; /* Twitter-like notification red */
  color: white;
  border-radius: 50%;
  padding: 2px 6px;
  font-size: 0.7em;
  font-weight: bold;
  min-width: 8px;
  text-align: center;
}

.notifications-icon:hover {
  color: #1da1f2;
}
</style>
